import { useState, useMemo, useEffect, useCallback, useRef } from 'react';

/**
 * Resumen por mes + caché de detalle al expandir.
 * @param {() => object} buildParams
 * @param {(params: object) => Promise<{ success?: boolean, data?: object }>} fetchSummary
 * @param {(params: object) => Promise<{ success?: boolean, data?: unknown, meta?: object }>} fetchMonth
 * @param {string} [searchTerm]
 * @param {(response: object) => unknown[]} [mapMonthRows]
 */
export function useLazyMonthList({
  buildParams,
  fetchSummary,
  fetchMonth,
  searchTerm = '',
  mapMonthRows = (res) => (Array.isArray(res.data) ? res.data : []),
  onMonthMeta,
}) {
  const [monthSummaries, setMonthSummaries] = useState([]);
  const [listStats, setListStats] = useState(null);
  const [creatorOptions, setCreatorOptions] = useState([]);
  const [monthCache, setMonthCache] = useState({});
  const [loadingMonthKeys, setLoadingMonthKeys] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(true);
  const monthCacheRef = useRef({});
  const loadingMonthRef = useRef({});

  monthCacheRef.current = monthCache;
  loadingMonthRef.current = loadingMonthKeys;

  const listParams = useMemo(() => buildParams(), [buildParams]);
  const listParamsKey = useMemo(() => JSON.stringify(listParams), [listParams]);

  useEffect(() => {
    let cancelled = false;
    const delay = searchTerm.trim() ? 400 : 0;
    const timer = setTimeout(async () => {
      setLoadingSummary(true);
      try {
        const response = await fetchSummary(listParams);
        if (cancelled) return;
        if (response?.success) {
          const payload = response.data;
          setMonthSummaries(payload?.months || []);
          setListStats(payload?.stats ?? null);
          setCreatorOptions(payload?.creators || []);
          setMonthCache({});
          monthCacheRef.current = {};
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error al cargar resumen por mes:', error);
        }
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [listParamsKey, listParams, fetchSummary, searchTerm]);

  const reloadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const response = await fetchSummary(listParams);
      if (response?.success) {
        const payload = response.data;
        setMonthSummaries(payload?.months || []);
        setListStats(payload?.stats ?? null);
        setCreatorOptions(payload?.creators || []);
        setMonthCache({});
        monthCacheRef.current = {};
      }
    } finally {
      setLoadingSummary(false);
    }
  }, [listParams, fetchSummary]);

  const loadMonthDetail = useCallback(
    async (period) => {
      if (monthCacheRef.current[period] || loadingMonthRef.current[period]) {
        return;
      }
      setLoadingMonthKeys((prev) => ({ ...prev, [period]: true }));
      loadingMonthRef.current = { ...loadingMonthRef.current, [period]: true };
      try {
        const response = await fetchMonth({ ...listParams, issue_month: period });
        if (response?.success) {
          const rows = mapMonthRows(response);
          setMonthCache((prev) => {
            const next = { ...prev, [period]: rows };
            monthCacheRef.current = next;
            return next;
          });
          if (onMonthMeta && response.meta) {
            onMonthMeta(response.meta, period);
          }
        }
      } catch (error) {
        console.error('Error al cargar detalle del mes:', error);
      } finally {
        setLoadingMonthKeys((prev) => {
          const next = { ...prev };
          delete next[period];
          loadingMonthRef.current = next;
          return next;
        });
      }
    },
    [listParams, fetchMonth, mapMonthRows, onMonthMeta]
  );

  const getMonthItems = useCallback((period) => monthCache[period], [monthCache]);

  const isMonthLoading = useCallback((period) => Boolean(loadingMonthKeys[period]), [loadingMonthKeys]);

  return {
    monthSummaries,
    listStats,
    creatorOptions,
    loadingSummary,
    loadMonthDetail,
    getMonthItems,
    isMonthLoading,
    reloadSummary,
    listParams,
  };
}
